import React, { useEffect, useMemo, useState } from 'react';
import Layout from 'Layout';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getCategoryTree, getPostById, searchPosts } from './function/api';
import { findCategoryPathIds, findPostInCategoryTreeBySlug, getLocalizedValue, mapCategoryTreeToPreviewGroups } from './function/converter';
import { CategoryTreeAPIResponse, CategoryTreePostSummary, HelpNavigationState, Post } from './function/type';
import './styles.scss';

const HelpCenter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = (i18n.language || 'en') as 'en' | 'vi';
  const navigationState = location.state as HelpNavigationState | null;

  const [categoryTree, setCategoryTree] = useState<CategoryTreeAPIResponse[]>([]);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Post | null>(navigationState?.post || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Post[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningArticleId, setIsOpeningArticleId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchErrorMessage, setSearchErrorMessage] = useState('');

  const categories = useMemo(() => mapCategoryTreeToPreviewGroups(categoryTree), [categoryTree]);
  const categoryGroupsById = useMemo(() => {
    return new Map(categories.map((category) => [category.categoryId, category]));
  }, [categories]);

  const selectedCategory = useMemo(() => {
    if (!categories.length) {
      return null;
    }

    return categories.find((category) => category.categoryId === selectedCategoryId) || categories[0];
  }, [categories, selectedCategoryId]);

  const findCategoryNode = (nodes: CategoryTreeAPIResponse[], targetId: number): CategoryTreeAPIResponse | null => {
    for (const node of nodes) {
      if (node.id === targetId) {
        return node;
      }

      const childNode = findCategoryNode(node.children || [], targetId);

      if (childNode) {
        return childNode;
      }
    }

    return null;
  };

  const selectedCategoryNode = useMemo(() => {
    if (!selectedCategoryId) {
      return categoryTree[0] || null;
    }

    return findCategoryNode(categoryTree, selectedCategoryId) || categoryTree[0] || null;
  }, [categoryTree, selectedCategoryId]);

  const selectedCategoryArticles = useMemo(() => {
    if (!selectedCategoryNode) {
      return [];
    }

    const articleMap = new Map<number, CategoryTreePostSummary>();

    const collectArticles = (category: CategoryTreeAPIResponse) => {
      const currentArticles = categoryGroupsById.get(category.id)?.articles || [];

      currentArticles.forEach((article) => {
        articleMap.set(article.id, article);
      });

      category.children?.forEach(collectArticles);
    };

    collectArticles(selectedCategoryNode);

    return Array.from(articleMap.values());
  }, [categoryGroupsById, selectedCategoryNode]);

  const activeCategory = useMemo(() => {
    if (!selectedArticle) {
      return selectedCategory;
    }

    return categories.find((category) => category.categoryId === selectedArticle.category_id) || selectedCategory;
  }, [categories, selectedArticle, selectedCategory]);

  const relatedArticles = useMemo(() => {
    if (!activeCategory || !selectedArticle) {
      return [];
    }

    return activeCategory.articles.filter((article) => article.id !== selectedArticle.id);
  }, [activeCategory, selectedArticle]);

  const getArticleHoverText = (article: CategoryTreePostSummary | Post): string => {
    return getLocalizedValue(article.excerpt, lang) || getLocalizedValue(article.title, lang);
  };

  const syncArticleSlug = (slug?: string) => {
    const nextSearchParams = new URLSearchParams(location.search);

    if (slug) {
      nextSearchParams.set('slug', slug);
    } else {
      nextSearchParams.delete('slug');
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : '',
      },
      {
        replace: true,
        state: location.state,
      },
    );
  };

  const openResolvedArticle = (fullPost: Post) => {
    setSelectedArticle(fullPost);
    setSelectedCategoryId(fullPost.category_id);
    setExpandedCategoryIds((previousIds) => {
      const categoryPath = findCategoryPathIds(categoryTree, fullPost.category_id);
      return Array.from(new Set([...previousIds, ...categoryPath]));
    });
    setIsSearching(false);
    setSearchQuery('');
    syncArticleSlug(fullPost.slug);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchPosts = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const allCategories = await getCategoryTree();

        if (!isMounted) {
          return;
        }

        setCategoryTree(allCategories);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(t('help_load_failed', 'Unable to load help articles right now.'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, [t]);

  useEffect(() => {
    const keyword = searchQuery.trim();

    if (!keyword) {
      setSearchResults([]);
      setIsSearchLoading(false);
      setSearchErrorMessage('');
      return undefined;
    }

    let isCancelled = false;

    setIsSearchLoading(true);
    setSearchErrorMessage('');

    const timeoutId = window.setTimeout(async () => {
      try {
        const posts = await searchPosts(keyword, 1, 20);

        if (isCancelled) {
          return;
        }

        setSearchResults(posts);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setSearchResults([]);
        setSearchErrorMessage(t('help_load_failed', 'Unable to load help articles right now.'));
      } finally {
        if (!isCancelled) {
          setIsSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery, t]);

  useEffect(() => {
    if (!categories.length) {
      return;
    }

    setExpandedCategoryIds((previousIds) => {
      if (previousIds.length > 0) {
        return previousIds;
      }

      if (selectedArticle?.category_id) {
        return findCategoryPathIds(categoryTree, selectedArticle.category_id);
      }

      if (navigationState?.selectedCategoryId) {
        const selectedPath = findCategoryPathIds(categoryTree, navigationState.selectedCategoryId);

        if (selectedPath.length > 0) {
          return selectedPath;
        }
      }

      if (navigationState?.expandedCategoryIds?.length) {
        return navigationState.expandedCategoryIds;
      }

      return categoryTree[0] ? [`cat-${categoryTree[0].id}`] : [categories[0].id];
    });

    setSelectedCategoryId((previousId) => previousId ?? selectedArticle?.category_id ?? navigationState?.selectedCategoryId ?? categories[0].categoryId);
  }, [categories, categoryTree, navigationState?.expandedCategoryIds, navigationState?.selectedCategoryId, selectedArticle]);

  useEffect(() => {
    const slug = searchParams.get('slug')?.trim();

    if (!slug || !categoryTree.length) {
      return;
    }

    if (selectedArticle?.slug === slug) {
      return;
    }

    const matchedPost = findPostInCategoryTreeBySlug(categoryTree, slug);

    if (!matchedPost) {
      return;
    }

    let isCancelled = false;

    const openArticleFromSlug = async () => {
      setIsOpeningArticleId(matchedPost.id);
      setErrorMessage('');

      try {
        const fullPost = await getPostById(matchedPost.id);

        if (isCancelled) {
          return;
        }

        openResolvedArticle(fullPost);
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage(t('help_load_failed', 'Unable to load help articles right now.'));
        }
      } finally {
        if (!isCancelled) {
          setIsOpeningArticleId(null);
        }
      }
    };

    openArticleFromSlug();

    return () => {
      isCancelled = true;
    };
  }, [categoryTree, searchParams, selectedArticle?.slug, t]);

  const toggleCategory = (categoryId: number) => {
    setExpandedCategoryIds((previousIds) =>
      previousIds.includes(`cat-${categoryId}`)
        ? previousIds.filter((id) => id !== `cat-${categoryId}`)
        : [...previousIds, `cat-${categoryId}`]
    );
  };

  const openCategory = (categoryId: number) => {
    const categoryPath = findCategoryPathIds(categoryTree, categoryId);

    setSelectedCategoryId(categoryId);
    setSelectedArticle(null);
    setIsSearching(false);
    setSearchQuery('');
    setExpandedCategoryIds((previousIds) =>
      Array.from(new Set([...previousIds, ...categoryPath]))
    );
    syncArticleSlug();
  };

  const goToArticle = async (article: CategoryTreePostSummary) => {
    setIsOpeningArticleId(article.id);

    try {
      const fullPost = await getPostById(article.id);
      openResolvedArticle(fullPost);
    } catch (error) {
      setErrorMessage(t('help_load_failed', 'Unable to load help articles right now.'));
    } finally {
      setIsOpeningArticleId(null);
    }
  };

  const goBackToList = () => {
    setSelectedArticle(null);
    setIsSearching(false);
    setSearchQuery('');
    syncArticleSlug();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsSearching(value.trim().length > 0);

    if (!value.trim()) {
      setSearchErrorMessage('');
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const renderSidebarCategory = (category: CategoryTreeAPIResponse, depth = 0): React.ReactNode => {
    const isExpanded = expandedCategoryIds.includes(`cat-${category.id}`);
    const isActive = selectedCategoryId === category.id && !isSearching && !selectedArticle;
    const hasChildren = (category.children || []).length > 0;

    return (
      <li key={category.id} className="help-page__tree-item">
        <div className={`help-page__tree-row ${isActive ? 'help-page__tree-row--active' : ''}`} style={{ paddingLeft: `${depth * 16}px` }}>
          {hasChildren ? (
            <button
              type="button"
              className="help-page__tree-toggle"
              onClick={() => toggleCategory(category.id)}
              aria-label={isExpanded ? 'Collapse category' : 'Expand category'}
            >
              <svg className={`help-page__cat-arrow ${isExpanded ? 'help-page__cat-arrow--open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          ) : (
            <span className="help-page__tree-spacer" />
          )}

          <button
            type="button"
            className={`help-page__tree-link ${hasChildren ? 'help-page__tree-link--section' : 'help-page__tree-link--clickable'}`}
            onClick={() => {
              openCategory(category.id);
            }}
          >
            {getLocalizedValue(category.name, lang)}
          </button>
        </div>

        {hasChildren && isExpanded && (
          <ul className="help-page__tree-list">
            {category.children?.map((child) => renderSidebarCategory(child, depth + 1))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <Layout>
      <Helmet>
        <title>{selectedArticle ? `${getLocalizedValue(selectedArticle.title, lang)} - ${t('help_page_title')} - Lumir` : `${t('help_page_title')} - Lumir`}</title>
        <meta name="description" content={selectedArticle ? getLocalizedValue(selectedArticle.excerpt, lang) || getLocalizedValue(selectedArticle.title, lang) : t('help_seo_desc')} />
        <link rel="canonical" href="/help" />
      </Helmet>

      <div className="help-page">
        <div className="help-page__hero">
          <h1 className="help-page__hero-title">{t('help_hero_title')}</h1>
          <form className="help-page__search-form" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              className="help-page__search-input"
              placeholder={t('help_search_placeholder')}
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button type="submit" className="help-page__search-btn" aria-label="Search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          </form>
        </div>

        <div className="help-page__body">
          <aside className="help-page__sidebar">
            <ul className="help-page__tree-list">
              {categoryTree.map((category) => renderSidebarCategory(category))}
            </ul>
          </aside>

          <main className="help-page__main">
            {isLoading || isOpeningArticleId ? (
              <p className="help-page__no-results">{t('help_loading', 'Loading help articles...')}</p>
            ) : errorMessage ? (
              <p className="help-page__no-results">{errorMessage}</p>
            ) : isSearching ? (
              <>
                <h2 className="help-page__section-title">
                  {t('help_search_results')} "{searchQuery}"
                </h2>
                {isSearchLoading ? (
                  <p className="help-page__no-results">{t('help_loading', 'Loading help articles...')}</p>
                ) : searchErrorMessage ? (
                  <p className="help-page__no-results">{searchErrorMessage}</p>
                ) : searchResults.length > 0 ? (
                  <ul className="help-page__article-list">
                    {searchResults.map((article) => (
                      <li key={article.id} className="help-page__article-item" onClick={() => goToArticle(article)}>
                        <span className="help-page__article-title" title={getArticleHoverText(article)}>
                          {getLocalizedValue(article.title, lang)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="help-page__no-results">{t('help_no_results')}</p>
                )}
              </>
            ) : selectedArticle && activeCategory ? (
              <>
                <div className="help-page__breadcrumb">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={goBackToList}
                    onKeyDown={(e) => e.key === 'Enter' && goBackToList()}
                  >
                    {t('help_page_title')}
                  </span>
                  <span className="help-page__breadcrumb-sep">›</span>
                  <span>{getLocalizedValue(activeCategory.title, lang)}</span>
                  <span className="help-page__breadcrumb-sep">›</span>
                  <span>{getLocalizedValue(selectedArticle.title, lang)}</span>
                </div>

                <div
                  className="help-page__article-detail"
                  dangerouslySetInnerHTML={{
                    __html: getLocalizedValue(selectedArticle.content, lang),
                  }}
                />

                {relatedArticles.length > 0 && (
                  <div className="help-page__related">
                    <h3 className="help-page__related-title">{t('help_related_articles')}</h3>
                    <ul className="help-page__article-list">
                      {relatedArticles.map((article) => (
                        <li key={article.id} className="help-page__article-item" onClick={() => goToArticle(article)}>
                          <span className="help-page__article-title" title={getArticleHoverText(article)}>
                            {getLocalizedValue(article.title, lang)}
                            {isOpeningArticleId === article.id ? ` ${t('help_loading', 'Loading help articles...')}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : selectedCategoryNode ? (
              <>
                <h2 className="help-page__section-title">{getLocalizedValue(selectedCategoryNode.name, lang)}</h2>
                <ul className="help-page__article-list">
                  {selectedCategoryArticles.map((article) => (
                    <li key={article.id} className="help-page__article-item" onClick={() => goToArticle(article)}>
                      <span className="help-page__article-title" title={getArticleHoverText(article)}>
                        {getLocalizedValue(article.title, lang)}
                        {isOpeningArticleId === article.id ? ` ${t('help_loading', 'Loading help articles...')}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
                {selectedCategoryArticles.length === 0 && (
                  <p className="help-page__no-results">{t('help_no_results')}</p>
                )}
              </>
            ) : (
              <p className="help-page__no-results">{t('help_no_results')}</p>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default HelpCenter;
